import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AxiosError } from 'axios';
import CustomButton from '../reusable/CustomButton';
import CustomTable, { TableHeaderData } from '../reusable/CustomTable';
import Panel from '../reusable/Panel';
import PageHeader from '../reusable/PageHeader';
import TableSkeleton from '../reusable/TableSkeleton';
import useUserDetails from '../hooks/useUserDetails';
import useErrorModal from '../hooks/useErrorModal';
import { NAVIGATION_LINKS_DATA } from '../../constants';
import { getSjwPhotoUploadSchools, SjwSchoolSummary } from '../../services/sjwPhotoUploadService';
import { formatDate } from '../../utils';
import SjwPhotoUploadStatus, { SjwPhotoUploadStatusValue } from './SjwPhotoUploadStatus';

import './SjwPhotoUploadOverview.css';

interface SchoolRow {
  id: string;
  name: string;
  status: SjwPhotoUploadStatusValue;
  dueDate: string;
}

const tableHeaderData: TableHeaderData<SchoolRow>[] = [
  { key: 'name', label: 'School', icons: ['sort'] },
  { key: 'status', label: 'Status', icons: ['sort'] },
  { key: 'dueDate', label: 'Due date' },
  { key: 'actions', label: 'Actions' },
];

const toRow = (summary: SjwSchoolSummary): SchoolRow => ({
  id: summary.school_id,
  name: summary.school_name,
  status: summary.status,
  dueDate: summary.due_date ? formatDate(summary.due_date) : '',
});

const SjwPhotoUploadOverview = () => {
  const navigate = useNavigate();
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();
  const { handleOpen: handleOpenErrorModal } = useErrorModal();

  const [rows, setRows] = useState<SchoolRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!userDetails) return;
    setIsLoading(true);
    getSjwPhotoUploadSchools(userDetails.eoaId, userDetails.access_token)
      .then((response) => {
        setRows(response.schools.map(toRow));
      })
      .catch((error) => {
        handleOpenErrorModal('Could not load photo upload status.', error as AxiosError);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [userDetails]);

  const renderActionButton = (row: SchoolRow) => (
    <CustomButton
      variant="primary"
      size="small"
      buttonText="Edit"
      buttonAction={() => navigate(`${NAVIGATION_LINKS_DATA.SJW_PHOTO_UPLOAD.path}/${row.id}`)}
    />
  );

  return (
    <>
      <PageHeader title="Student Journey Workshop photo upload" />
      <div className="main-content">
        <Panel className="sjw-photo-upload-overview-panel">
          <h2 className="sjw-photo-upload-overview-panel-title">Select a school</h2>
          <p className="sjw-photo-upload-overview-panel-description">
            Choose the school for which you need to upload workshop photos.
          </p>
          <CustomTable
            tableHeaderData={tableHeaderData.map((header) => {
              if (header.key === 'status') {
                return {
                  ...header,
                  renderCell: (row: SchoolRow) => <SjwPhotoUploadStatus status={row.status} />,
                };
              }
              if (header.key === 'actions') {
                return { ...header, renderCell: renderActionButton };
              }
              return header;
            })}
            tableBodyData={isLoading ? undefined : rows}
          >
            {isLoading && <TableSkeleton rowsNumber={3} colsNumber={tableHeaderData.length} />}
          </CustomTable>
        </Panel>
      </div>
    </>
  );
};

export default SjwPhotoUploadOverview;
